import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, Plus, Trash2, Settings, UserPlus, X, Loader2, Trophy, BarChart3, Download } from 'lucide-react';
import { toast } from 'sonner';
import TeamAnalytics from './TeamAnalytics';

import { API } from '../config/api';

export default function TeamManager({ onTeamSelect }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newTeam, setNewTeam] = useState({ name: '', description: '', sport: '', color: '#02b5ff' });
  const [creating, setCreating] = useState(false);
  const [downloadingTeam, setDownloadingTeam] = useState(null);

  useEffect(() => { fetchTeams(); }, []);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/teams`, { headers });
      setTeams(res.data || []);
    } catch (err) {
      // Don't show error toast for empty teams - just set empty array
      console.error('Teams fetch error:', err);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadTeamReports = async (team, e) => {
    e.stopPropagation();
    if (!team.name || (team.member_count || 0) === 0) {
      toast.error('No players in this team to download reports for');
      return;
    }
    
    setDownloadingTeam(team.id);
    try {
      const token = localStorage.getItem('token');
      
      // First validate
      const validateRes = await axios.get(
        `${API}/reports/bulk-download/validate?team_name=${encodeURIComponent(team.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!validateRes.data.can_bulk_download) {
        if (validateRes.data.players_missing_dob?.length > 0) {
          toast.error(`${validateRes.data.players_missing_dob.length} player(s) missing DOB. Update their profiles first.`);
        } else if (validateRes.data.total_players === 0) {
          toast.error('No players found in this team');
        }
        return;
      }
      
      // Download the reports
      const response = await fetch(
        `${API}/reports/bulk-download?team_name=${encodeURIComponent(team.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reports_${team.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded ${validateRes.data.players_ready} reports for ${team.name}`);
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || 'Failed to download reports');
      }
    } catch (err) {
      toast.error('Failed to download team reports');
    } finally {
      setDownloadingTeam(null);
    }
  };

  const createTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name.trim()) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/teams`, newTeam, { headers });
      setTeams([...teams, res.data]);
      setNewTeam({ name: '', description: '', sport: '', color: '#02b5ff' });
      setShowCreate(false);
      toast.success('Team created!');
    } catch (err) {
      toast.error('Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Delete this team?')) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/teams/${teamId}`, { headers });
      setTeams(teams.filter(t => t.id !== teamId));
      toast.success('Team deleted');
    } catch (err) {
      toast.error('Failed to delete team');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  // Show team analytics if a team is selected
  if (selectedTeam) {
    return (
      <TeamAnalytics 
        team={selectedTeam} 
        onBack={() => { setSelectedTeam(null); fetchTeams(); }}
        onUpdate={fetchTeams}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="team-manager">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Users className="h-5 w-5 text-[#02b5ff]" />
          Teams & Squads
        </h3>
        <Button
          onClick={() => setShowCreate(!showCreate)}
          className="h-9 px-4 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] text-white rounded-full text-sm"
          data-testid="create-team-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Team
        </Button>
      </div>

      {showCreate && (
        <form onSubmit={createTeam} className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-4 space-y-3">
          <input
            type="text"
            placeholder="Team name"
            value={newTeam.name}
            onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newTeam.description}
            onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Sport"
              value={newTeam.sport}
              onChange={(e) => setNewTeam({ ...newTeam, sport: e.target.value })}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
            />
            <input
              type="color"
              value={newTeam.color}
              onChange={(e) => setNewTeam({ ...newTeam, color: e.target.value })}
              className="w-12 h-10 bg-zinc-800 border border-zinc-700 rounded-lg cursor-pointer"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={creating} className="flex-1 bg-[#02b5ff] text-white rounded-lg">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Team'}
            </Button>
            <Button type="button" onClick={() => setShowCreate(false)} variant="outline" className="border-zinc-700 text-zinc-400">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {teams.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No teams created yet</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {teams.map((team) => (
            <div
              key={team.id}
              className="bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 rounded-xl p-4 cursor-pointer transition-all group"
              onClick={() => setSelectedTeam(team)}
              data-testid={`team-card-${team.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${team.color}20`, borderColor: team.color, borderWidth: 2 }}
                  >
                    <Users className="h-5 w-5" style={{ color: team.color }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white group-hover:text-[#02b5ff] transition-colors">
                      {team.name}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      {team.sport && <span className="text-[#02b5ff]">{team.sport}</span>}
                      <span>•</span>
                      <span>{team.member_count || 0} athletes</span>
                    </div>
                  </div>
                </div>
              <div className="flex items-center gap-2">
                  {/* Download Team Reports Button */}
                  <button
                    onClick={(e) => downloadTeamReports(team, e)}
                    disabled={downloadingTeam === team.id}
                    className="p-2 text-zinc-500 hover:text-[#02b5ff] opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                    title="Download team reports"
                  >
                    {downloadingTeam === team.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteTeam(team.id); }}
                    className="p-2 text-zinc-500 hover:text-[#ff025f] opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {team.description && (
                <p className="text-xs text-zinc-500 mt-2 ml-13">{team.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
